import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { HERO_LINES as DEFAULT_HERO_LINES } from "../data/content";
import { useContent } from "../data/ContentContext";

const wordVariants = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.2 + i * 0.08, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Hero = () => {
  const { content } = useContent();
  const HERO_LINES = content?.hero?.lines || DEFAULT_HERO_LINES;
  const { scrollY } = useScroll();
  const yParallax = useTransform(scrollY, [0, 800], [0, -160]);

  useEffect(() => {
    const v = document.getElementById("hero-bg-video");
    if (v) v.play().catch(() => {});
  }, []);

  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  const goWork = () => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative min-h-[88vh] md:min-h-[92vh] flex items-end pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 lg:px-14 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background video — silent backdrop of recent work */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <motion.video
          id="hero-bg-video"
          src="/media/video/highlights-2021.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/media/img/d2020-cityscape-1.jpg"
          style={{ y: yParallax }}
          className="absolute inset-0 w-full h-[115%] object-cover"
          data-testid="hero-bg-video"
        />
        {/* Lighter overlay so the video is clearly visible */}
        <div className="absolute inset-0 bg-black/35" />
        {/* Soft top + bottom fade for legibility, no full-page wash */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[var(--bg)] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-60 bg-gradient-to-t from-[var(--bg)]/95 via-[var(--bg)]/40 to-transparent" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto w-full">
        <div className="grid grid-cols-12 gap-6 items-end">
          {/* Compact text block, bottom-left */}
          <div className="col-span-12 lg:col-span-7">
            {HERO_LINES.map((line, i) => (
              <div key={i} className="overflow-hidden">
                <motion.h1
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={wordVariants}
                  className="hero-word text-white text-[22vw] sm:text-[18vw] md:text-[14vw] lg:text-[11vw] xl:text-[10vw] drop-shadow-[0_4px_30px_rgba(0,0,0,0.45)]"
                  data-testid={`hero-line-${i}`}
                >
                  {line}
                </motion.h1>
              </div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button onClick={goContact} className="cta-pill" data-testid="hero-cta-contact">
                <span>Get in touch</span>
                <ArrowRight size={16} />
              </button>
              <button
                onClick={goWork}
                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold text-white link-underline"
                data-testid="hero-cta-work"
              >
                <span>See the work</span>
                <ArrowDown size={16} />
              </button>
            </motion.div>
          </div>
        </div>

        {/* Animated scroll indicator pointing to About */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
          className="hidden md:flex absolute bottom-6 right-8 lg:right-14 z-10 flex-col items-center gap-2 text-white hover:text-[var(--accent)] transition-colors"
          aria-label="Scroll to About"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] uppercase tracking-[0.32em] font-semibold">About</span>
          <span className="block w-px h-12 bg-current relative overflow-hidden">
            <span className="block w-px h-6 bg-[var(--accent)] absolute top-0 left-0 animate-scroll-cue" />
          </span>
          <ArrowDown size={14} className="animate-bounce-soft" />
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
